/**
 * Utilities for caching data in localStorage with expiration
 */

// Default cache expiry time (24 hours)
const DEFAULT_CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

/**
 * Get data from cache if it exists and hasn't expired
 * 
 * @param {string} key - Cache key
 * @param {number} expiryMs - Expiry time in milliseconds
 * @returns {Object|null} - Cached data or null if not found or expired
 */
export const getCachedData = (key, expiryMs = DEFAULT_CACHE_EXPIRY_MS) => {
  try {
    const cachedData = localStorage.getItem(key);
    if (!cachedData) return null;
    
    const { data, timestamp } = JSON.parse(cachedData);
    const isExpired = Date.now() - timestamp > expiryMs;
    
    return isExpired ? null : data;
  } catch (err) {
    console.warn(`Error reading from cache (${key}):`, err);
    return null;
  }
};

/**
 * Save data to cache with timestamp
 * 
 * @param {string} key - Cache key
 * @param {Object} data - Data to cache
 * @returns {boolean} - Whether caching was successful
 */
export const setCachedData = (key, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    return true;
  } catch (err) {
    console.warn(`Error writing to cache (${key}):`, err);
    return false;
  }
};

/**
 * Clear specific cache entry
 * 
 * @param {string} key - Cache key to clear
 */
export const clearCacheEntry = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn(`Error clearing cache entry (${key}):`, err);
  }
};

/**
 * Clear all cache entries with a specific prefix
 * 
 * @param {string} prefix - Prefix for cache keys to clear
 */
export const clearCacheByPrefix = (prefix) => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (err) {
    console.warn(`Error clearing cache by prefix (${prefix}):`, err);
  }
};

/**
 * Get all cache keys
 * 
 * @returns {string[]} - Array of cache keys
 */
export const getCacheKeys = () => {
  try {
    return Object.keys(localStorage);
  } catch (err) {
    console.warn('Error getting cache keys:', err);
    return [];
  }
};

// Cache keys
export const CACHE_KEYS = {
  AGGREGATE_DATA: 'electoral-grants-aggregate-data',
  ELECTORATE_DATA: 'electoral-grants-electorate-data-',
  COMPARISON_DATA: 'electoral-grants-comparison-data-',
  APP_DATA: 'electoral-grants-app-data'
}; 